import type { WidgetPageStats, WidgetPagesSortField, WidgetPreviewModeSummary } from '@shared/api';

export const PAGES_PAGE_SIZE = 20;

export const MIN_VISITORS_OPTIONS = [0, 100, 500, 1000] as const;
export type MinVisitorsOption = (typeof MIN_VISITORS_OPTIONS)[number];
export const DEFAULT_MIN_VISITORS: MinVisitorsOption = 100;

export type PagesSort = { field: WidgetPagesSortField; desc: boolean };
export const DEFAULT_PAGES_SORT: PagesSort = { field: 'visitors', desc: true };

/** Hosts and paths go into tooltips as-is; i18next would otherwise turn "/" into "&#x2F;". */
export const NO_ESCAPE = { interpolation: { escapeValue: false } } as const;

/** Clicking a header sorts by it, clicking it again flips the direction. Text sorts ascending first. */
export const nextSort = (current: PagesSort, field: WidgetPagesSortField): PagesSort => {
  if (current.field === field) return { field, desc: !current.desc };
  return { field, desc: field !== 'page' };
};

export type PreviewStatus = 'static' | 'none' | 'auto' | 'edited';

// Manual edits and regenerations both move updated_at away from created_at.
const EDITED_THRESHOLD_MS = 60_000;

export const getPreviewStatus = (
  page: Pick<WidgetPageStats, 'preview'>,
  previewMode: WidgetPreviewModeSummary | null | undefined,
): PreviewStatus => {
  if (previewMode === 'static') return 'static';
  if (!page.preview) return 'none';
  const created = Date.parse(page.preview.created_at);
  const updated = Date.parse(page.preview.updated_at);
  return updated - created > EDITED_THRESHOLD_MS ? 'edited' : 'auto';
};

/** A percent change needs at least this many visitors in the previous period to mean anything. */
export const MIN_DELTA_BASE = 20;

/** Percent change against the previous period; null when the base is missing or too small. */
export const getDeltaPercent = (
  current: number,
  previous: number | null | undefined,
): number | null => {
  if (previous === null || previous === undefined || previous < MIN_DELTA_BASE) return null;
  return ((current - previous) / previous) * 100;
};

export const formatDelta = (delta: number | null): string => {
  if (delta === null || !Number.isFinite(delta)) return '';
  const rounded = Math.round(delta);
  if (rounded === 0) return '0%';
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)}%`;
};

/** Change of a rate in percentage points, e.g. 0.53% → 0.91% gives "+0.4 п.п." without the unit. */
export const formatPointsDelta = (current: number, previous: number | null | undefined): string => {
  if (previous === null || previous === undefined) return '';
  const delta = Math.round((current - previous) * 10) / 10;
  if (delta === 0) return '0';
  return `${delta > 0 ? '+' : '−'}${Math.abs(delta)}`;
};

export const formatCount = (value: number, locale = 'ru-RU'): string =>
  new Intl.NumberFormat(locale).format(value);

/** Rates below 1% keep two decimals so 0.53% and 0.91% stay distinguishable. */
export const formatRate = (value: number): string => {
  if (!Number.isFinite(value)) return '0%';
  const digits = value > 0 && value < 1 ? 2 : 1;
  return `${new Intl.NumberFormat('en', { maximumFractionDigits: digits }).format(value)}%`;
};

export const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en', { maximumFractionDigits: 1 }).format(value);

export const formatPotential = (value: number): string => {
  if (value <= 0) return '—';
  const digits = value >= 10 ? 0 : 1;
  return `+${new Intl.NumberFormat('en', { maximumFractionDigits: digits }).format(value)}`;
};
